import React from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import { Send } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';

import Dialog from '@dashboard/components/Dialog';
import Button from '@dashboard/components/Button';
import { Button as UiButton } from '@dashboard/components/ui/button';
import Link from '@dashboard/components/Link';
import Spinner from '@dashboard/components/Spinner';
import CanUseFeature, { useCanUseFeature } from '@dashboard/components/CanUseFeature';
import { FormElement, Label } from '@dashboard/components/Form';
import { TextInput, RichTextarea } from '@dashboard/components/Formik';
import { Input } from '@dashboard/components/ui/input';
import FormError from '@dashboard/components/Formik/components/FormError';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { toast } from '@dashboard/context/ToastContext';
import Features from '@dashboard/enums/features';
import { fetcher } from '@dashboard/services/request';

const slateHasText = message => {
  if (typeof message !== 'string' || message.trim().length === 0) {
    return false;
  }

  try {
    const nodes = JSON.parse(message);
    const hasText = node =>
      node.text ? node.text.trim().length > 0 : (node.children ?? []).some(hasText);

    return Array.isArray(nodes) && nodes.some(hasText);
  } catch {
    return true;
  }
};

const validationSchema = Yup.object({
  subject: Yup.string()
    .trim()
    .required('Add a subject')
    .max(200, 'Keep the subject under 200 characters'),
  message: Yup.string().test('has-text', 'Write a message', slateHasText),
});

export default function EmailCustomerDialog({
  customerEmail,
  livemode = true,
  objectLabel,
  endpoint,
  variables = [],
  queryKey,
  emailsPath,
  asButton = false,
  label = 'Email customer',
}) {
  const { token, seller, user } = useAuth();
  const history = useHistory();
  const queryClient = useQueryClient();
  const canUseEmailDomain = useCanUseFeature(Features.EMAIL_DOMAIN);
  const canEmailCustomer = useCanUseFeature(Features.EMAIL_CUSTOMER);

  const { mutateAsync: sendEmail } = useMutation({
    mutationFn: values =>
      fetcher(endpoint, token, {
        method: 'POST',
        body: JSON.stringify({ subject: values.subject, message: values.message }),
      }),
    onSettled: () => {
      if (queryKey) {
        queryClient.invalidateQueries({ queryKey });
      }
    },
  });

  const { mutateAsync: sendTestEmail, isPending: isSendingTest } = useMutation({
    mutationFn: values =>
      fetcher(endpoint, token, {
        method: 'POST',
        body: JSON.stringify({ subject: values.subject, message: values.message, test: true }),
      }),
  });

  const renderTrigger = ({ locked = false } = {}) =>
    asButton ? (
      <UiButton variant="outline" size="sm">
        {label}
        <Send />
        {locked && <LockClosedIcon />}
      </UiButton>
    ) : (
      <Link className="w-full flex items-center gap-x-2 cursor-pointer">
        <Send className="w-4 h-4" />
        {label}
      </Link>
    );

  if (!customerEmail) {
    return null;
  }

  if (!canEmailCustomer) {
    return (
      <CanUseFeature
        feature={Features.EMAIL_CUSTOMER}
        iconSize="small"
        showIcon={!asButton}
        inline={asButton}
      >
        {renderTrigger({ locked: true })}
      </CanUseFeature>
    );
  }

  const sendTest = async ({ values, validateForm, setTouched }) => {
    const errors = await validateForm();
    if (Object.keys(errors).length > 0) {
      setTouched({ subject: true, message: true });
      return;
    }

    try {
      await sendTestEmail(values);
      toast({ title: `Test email sent to ${user.email}` });
    } catch (error) {
      toast({ title: error?.message || 'Failed to send test email', type: 'error' });
    }
  };

  const storeName = seller?.displayName || seller?.name;

  const customSenderEmail =
    canUseEmailDomain && seller?.senderSignature?.confirmed === true
      ? seller.senderSignature.emailAddress
      : null;

  return (
    <Dialog trigger={renderTrigger()} title={label} confirmButton={null} cancelButton={null}>
      {({ close }) => (
        <Formik
          initialValues={{ subject: '', message: '' }}
          validationSchema={validationSchema}
          onSubmit={async (values, actions) => {
            try {
              const result = await sendEmail(values);
              close();
              if (result?.sentToSender) {
                toast({ title: `Test mode: email sent to ${result.sentTo}` });
                return;
              }
              toast({ title: 'Email sent' });
              if (emailsPath) {
                history.push(emailsPath);
              }
            } catch (error) {
              toast({
                title: error?.message || 'Failed to send email',
                type: 'error',
              });
            } finally {
              actions.setSubmitting(false);
            }
          }}
        >
          {({ values, setFieldValue, isSubmitting, validateForm, setTouched }) => (
            <Form className="flex flex-col gap-y-4 text-left">
              <FormElement className="!mb-0">
                <Label htmlFor="email-customer-to">To</Label>
                <Input
                  id="email-customer-to"
                  value={livemode ? customerEmail : user.email}
                  disabled
                />
                {!livemode && (
                  <p
                    className="mt-1 text-sm text-gray-500 dark:text-muted-foreground"
                    data-testid="test-mode-note"
                  >
                    For test {objectLabel}, the email goes to you, not to {customerEmail}.
                  </p>
                )}
              </FormElement>

              <FormElement className="!mb-0">
                <Label htmlFor="email-customer-subject">Subject</Label>
                <TextInput id="email-customer-subject" name="subject" placeholder="Subject" />
                <FormError name="subject" />
              </FormElement>

              <FormElement className="!mb-0">
                <div className="flex justify-between items-baseline">
                  <Label htmlFor="email-customer-message">Message</Label>
                  {variables.length > 0 && (
                    <span className="text-xs text-muted-foreground font-normal text-right ml-auto">
                      Insert variables by typing{' '}
                      <code className="font-mono bg-muted px-1 py-0.5 text-primary">{'{'}</code>
                    </span>
                  )}
                </div>
                <RichTextarea
                  id="email-customer-message"
                  name="message"
                  values={values}
                  setFieldValue={setFieldValue}
                  variables={variables}
                  placeholder="Message"
                />
                <FormError name="message" />
              </FormElement>

              <div
                className="rounded-md bg-muted/50 p-3 text-sm text-gray-500 dark:text-muted-foreground"
                data-testid="sender-note"
              >
                {customSenderEmail ? (
                  <>Sent from {customSenderEmail}.</>
                ) : (
                  <>
                    Sent as {storeName} from payments@checkoutpage.com. Replies go to{' '}
                    {seller?.supportEmail}.{' '}
                    <Link to="/settings/email" variant="secondary">
                      Send from your own domain instead
                    </Link>
                  </>
                )}
              </div>

              <div>
                <div className="flex justify-between gap-x-2">
                  <Button
                    type="button"
                    appearance="secondary"
                    onClick={() => sendTest({ values, validateForm, setTouched })}
                    disabled={isSubmitting || isSendingTest}
                  >
                    Send test email
                    {isSendingTest && <Spinner className="ml-2 h-4 w-4" />}
                  </Button>
                  <div className="flex gap-x-2">
                    <Button
                      type="button"
                      appearance="secondary"
                      // Keeps focus in the field, so its blur validation cannot grow the dialog under the tap.
                      onMouseDown={e => e.preventDefault()}
                      onClick={close}
                      disabled={isSubmitting || isSendingTest}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      appearance="primary"
                      disabled={isSubmitting || isSendingTest}
                    >
                      Send email
                      {isSubmitting && <Spinner className="ml-2 h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <small
                  className="mt-1 block truncate text-gray-800 dark:text-foreground"
                  data-testid="test-email-recipient"
                >
                  Sending to <strong>{user.email}</strong>
                </small>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </Dialog>
  );
}
